// Base44 API Client
// This client provides methods to interact with the backend API
import axios from 'axios';

// Default to the relative `/api` path so the bundle works in production
// (resolves to current origin → nginx /api/ proxy → Django) and in dev
// (CRA's `proxy` in package.json forwards /api/* to the local backend).
// An absolute localhost fallback here previously caused every login to
// appear as "wrong username or password" when REACT_APP_API_URL was
// unset at build time.
const API_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_URL,
});

const buildStreamingHeaders = () => {
  const headers = {};
  const token = localStorage.getItem('access_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const restaurantId = localStorage.getItem('current_restaurant_id');
  if (restaurantId) {
    headers['X-Restaurant-Id'] = restaurantId;
  }
  const activeEmployeeId = localStorage.getItem('active_employee_id');
  if (activeEmployeeId) {
    headers['X-Active-Employee-Id'] = activeEmployeeId;
  }
  return headers;
};

// Request interceptor: attach JWT token and restaurant context
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const restaurantId = localStorage.getItem('current_restaurant_id');
    if (restaurantId) {
      config.headers['X-Restaurant-Id'] = restaurantId;
    }
    const activeEmployeeId = localStorage.getItem('active_employee_id');
    if (activeEmployeeId) {
      config.headers['X-Active-Employee-Id'] = activeEmployeeId;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh queue to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

export const isAuthFailure = (error) => {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  const detail = error.response?.data?.detail;

  if (status === 401) return true;
  if (status !== 403) return false;

  return (
    code === 'token_not_valid'
    || detail === 'Authentication credentials were not provided.'
  );
};

// Response interceptor: handle expired/missing auth and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest || !isAuthFailure(error) || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        const response = await axios.post(`${API_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });
        const newToken = response.data.access;
        localStorage.setItem('access_token', newToken);
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    isRefreshing = false;
    window.location.href = '/login';
    return Promise.reject(error);
  }
);

// Agents API
const agents = {
  async listConversations(params = {}) {
    try {
      const agent_name = typeof params === 'string' ? params : params?.agent_name;
      const response = await apiClient.get(`/agents/${agent_name}/conversations/`);
      const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error listing conversations:', error);
      return [];
    }
  },
  async createConversation(params = {}) {
    try {
      const agent_name = typeof params === 'string' ? params : params?.agent_name;
      const response = await apiClient.post(`/agents/${agent_name}/conversations/`, params);
      return response.data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { id: null, messages: [] };
    }
  },
  async getConversation(id) {
    try {
      const response = await apiClient.get(`/conversations/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error getting conversation:', error);
      return { id: null, messages: [] };
    }
  },
  async addMessage(conversation, message = {}) {
    try {
      const conversationId = typeof conversation === 'object' ? conversation?.id : conversation;
      const content = typeof message === 'object' ? message?.content : message;
      const file = typeof message === 'object' ? message?.file : null;
      const files = typeof message === 'object' ? message?.files : null;

      if (file || (files && files.length)) {
        const form = new FormData();
        if (content) form.append('content', content);
        if (files && files.length) {
          Array.from(files).forEach((currentFile) => form.append('files', currentFile));
        } else {
          form.append('file', file);
        }
        const response = await apiClient.post(
          `/conversations/${conversationId}/messages/`,
          form,
        );
        return response.data;
      }

      const response = await apiClient.post(`/conversations/${conversationId}/messages/`, { content });
      return response.data;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  },
  async listMessages(conversationId, { afterId } = {}) {
    try {
      const q = afterId ? `?after_id=${afterId}` : '';
      const response = await apiClient.get(`/conversations/${conversationId}/messages/${q}`);
      return response.data?.messages ?? [];
    } catch (error) {
      console.error('Error listing messages:', error);
      return [];
    }
  },
  async streamMessages(conversationId, { afterId, afterActivityId, signal, onMessage, onActivity, onHeartbeat, onStatus } = {}) {
    const params = new URLSearchParams();
    if (afterId) params.set('after_id', String(afterId));
    if (afterActivityId) params.set('after_activity_id', String(afterActivityId));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${API_URL}/conversations/${conversationId}/messages/stream/${suffix}`, {
      headers: buildStreamingHeaders(),
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const flushLine = (line) => {
      if (!line.trim()) return;
      const packet = JSON.parse(line);
      if (packet.type === 'message' && packet.message) {
        onMessage?.(packet.message);
      } else if (packet.type === 'activity' && packet.activity) {
        onActivity?.(packet.activity);
      } else if (packet.type === 'heartbeat') {
        onHeartbeat?.(packet);
      } else if (packet.type === 'status') {
        onStatus?.(packet);
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        lines.forEach(flushLine);

        if (done) break;
      }

      if (buffer.trim()) {
        flushLine(buffer);
      }
    } finally {
      reader.releaseLock();
    }
  },
  subscribeToConversation(conversationId, callback) {
    // No-op — real-time not implemented
    return () => {};
  },
  async transcribe(audioBlob, language) {
    try {
      const formData = new FormData();
      const ext = audioBlob.type?.includes('webm') ? 'webm' : 'mp4';
      formData.append('audio', audioBlob, `recording.${ext}`);
      // Omit language to keep OpenAI auto-detection available unless the caller
      // has a strong reason to force one.
      if (language) formData.append('language', language);
      const response = await apiClient.post('/agents/transcribe/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  },
  async getMemoryGraph() {
    try {
      const response = await apiClient.get('/agents/memory/graph/');
      return response.data || { nodes: [], links: [], missing_links: [] };
    } catch (error) {
      console.error('Error loading memory graph:', error);
      return { nodes: [], links: [], missing_links: [] };
    }
  },
  async createMemoryPage(data = {}) {
    try {
      const response = await apiClient.post('/agents/memory/pages/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating memory page:', error);
      throw error;
    }
  },
  async getMemoryPage(slug) {
    try {
      const pathSlug = String(slug || "").split("/").map(encodeURIComponent).join("/");
      const response = await apiClient.get(`/agents/memory/pages/${pathSlug}/`);
      return response.data;
    } catch (error) {
      console.error('Error loading memory page:', error);
      throw error;
    }
  },
  async updateMemoryPage(slug, data = {}) {
    try {
      const pathSlug = String(slug || "").split("/").map(encodeURIComponent).join("/");
      const response = await apiClient.patch(`/agents/memory/pages/${pathSlug}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating memory page:', error);
      throw error;
    }
  },
};

const isPaginatedResponse = (data) => (
  data
  && typeof data === 'object'
  && Array.isArray(data.results)
  && Object.prototype.hasOwnProperty.call(data, 'next')
);

const fetchAllPages = async (url, params = {}) => {
  let nextUrl = url;
  let nextParams = params;
  const items = [];

  while (nextUrl) {
    const response = await apiClient.get(nextUrl, { params: nextParams });
    const data = response.data;

    if (!isPaginatedResponse(data)) {
      return Array.isArray(data) ? data : [];
    }

    items.push(...data.results);
    nextUrl = data.next;
    nextParams = undefined;
  }

  return items;
};

// Helper function to create CRUD operations for entities
const createEntityCRUD = (endpoint, defaultOrderBy = '-id') => ({
  async list(orderByOrOptions = defaultOrderBy, maybeOptions = {}) {
    try {
      const orderBy = typeof orderByOrOptions === 'string'
        ? orderByOrOptions
        : defaultOrderBy;
      const options = typeof orderByOrOptions === 'string'
        ? maybeOptions
        : (orderByOrOptions || {});
      return await fetchAllPages(`/${endpoint}/`, {
        ordering: orderBy,
        ...options,
      });
    } catch (error) {
      console.error(`Error listing ${endpoint}:`, error);
      return [];
    }
  },
  async create(data) {
    try {
      const response = await apiClient.post(`/${endpoint}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${endpoint}:`, error);
      throw error;
    }
  },
  async update(id, data) {
    try {
      const response = await apiClient.patch(`/${endpoint}/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      throw error;
    }
  },
  async delete(id) {
    try {
      const response = await apiClient.delete(`/${endpoint}/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw error;
    }
  },
});

// Helper function to add bulkCreate to entities
const addBulkCreate = (entityCRUD, endpoint) => ({
  ...entityCRUD,
  async bulkCreate(itemsData) {
    try {
      // Create items one by one (or implement bulk endpoint if backend supports it)
      const results = await Promise.all(
        itemsData.map(item => apiClient.post(`/${endpoint}/`, item))
      );
      return results.map(r => r.data);
    } catch (error) {
      console.error(`Error bulk creating ${endpoint}:`, error);
      throw error;
    }
  },
});

// Entities API
const entities = {
  Sale: addBulkCreate(createEntityCRUD('sales', '-date'), 'sales'),

  InventoryItem: addBulkCreate(createEntityCRUD('inventory', '-id'), 'inventory'),

  Supplier: createEntityCRUD('suppliers', 'name'),

  Task: addBulkCreate(createEntityCRUD('tasks', '-due_date'), 'tasks'),

  // Employee entity with bulkCreate
  Employee: addBulkCreate(createEntityCRUD('employees', '-id'), 'employees'),

  // Shift entity
  Shift: createEntityCRUD('shifts', '-date'),

  // Event entity
  Event: createEntityCRUD('events', '-date'),

  // Expense entity
  Expense: createEntityCRUD('expenses', '-date'),

  // ProductionLoss entity
  ProductionLoss: createEntityCRUD('production-losses', '-date'),

  Order: addBulkCreate(createEntityCRUD('orders', '-order_date'), 'orders'),

  // Restaurant entity
  Restaurant: createEntityCRUD('restaurants', '-id'),

  // Recipe entity with bulkCreate
  Recipe: addBulkCreate(createEntityCRUD('recipes', '-id'), 'recipes'),

  // ProductionBatch entity
  ProductionBatch: createEntityCRUD('production-batches', '-production_date'),

  // POS Integrations
  POSIntegration: createEntityCRUD('pos/integrations', 'provider'),
};

// Task-Recipe Allocations API
const taskRecipeAllocations = {
  listAll: async () => {
    const response = await apiClient.get('/task-recipe-allocations/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  listByTask: async (taskId) => {
    const response = await apiClient.get('/task-recipe-allocations/', { params: { task: taskId } });
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  listByRecipe: async (recipeId) => {
    const response = await apiClient.get('/task-recipe-allocations/', { params: { recipe: recipeId } });
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  create: async (data) => {
    const response = await apiClient.post('/task-recipe-allocations/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.patch(`/task-recipe-allocations/${id}/`, data);
    return response.data;
  },
  delete: async (id) => {
    await apiClient.delete(`/task-recipe-allocations/${id}/`);
  },
};

// POS-specific API calls
const pos = {
  getIntegrations: async () => {
    try {
      const response = await apiClient.get('/pos/integrations/');
      const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error fetching POS integrations:', error);
      return [];
    }
  },
  syncSales: async (integrationId) => {
    const response = await apiClient.post(`/pos/sync/${integrationId}/sales/`);
    return response.data;
  },
  syncMenu: async (integrationId) => {
    const response = await apiClient.post(`/pos/sync/${integrationId}/menu/`);
    return response.data;
  },
  gastronoviImport: async (startDate, endDate) => {
    const response = await apiClient.post('/pos/gastronovi/import/', {
      start_date: startDate,
      end_date: endDate || startDate,
    });
    return response.data;
  },
  getLogs: async (integrationId) => {
    const response = await apiClient.get(`/pos/logs/${integrationId}/`);
    return response.data;
  },
  startSumUpOAuth: (restaurantId) => {
    window.location.href = `${API_URL}/pos/oauth/sumup/authorize/?restaurant_id=${restaurantId}`;
  },
  startLightspeedOAuth: (restaurantId) => {
    window.location.href = `${API_URL}/pos/oauth/lightspeed/authorize/?restaurant_id=${restaurantId}`;
  },
  createIntegration: async (provider) => {
    const response = await apiClient.post('/pos/integrations/', { provider });
    return response.data;
  },
  uploadOrderbirdCSV: async (integrationId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/pos/orderbird/${integrationId}/upload-csv/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  uploadDailyReport: async (file, reportDate) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_date', reportDate);
    const response = await apiClient.post(
      '/pos/daily-report/upload-csv/',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  getDailyReportSales: async (date) => {
    const response = await apiClient.get('/pos/daily-report/sales/', { params: { date } });
    return response.data;
  },
  deleteDailyReportSales: async (date) => {
    const response = await apiClient.delete('/pos/daily-report/sales/', { params: { date } });
    return response.data;
  },
  confirmDailyProduct: async ({ name, unit_price, category }) => {
    const response = await apiClient.post('/pos/daily-report/products/confirm/', { name, unit_price, category });
    return response.data;
  },
  disconnect: async (integrationId) => {
    const response = await apiClient.patch(`/pos/integrations/${integrationId}/`, { is_active: false });
    return response.data;
  },
  saveCredentials: async (provider, clientId, clientSecret) => {
    const response = await apiClient.post(`/pos/credentials/${provider}/`, {
      client_id: clientId,
      client_secret: clientSecret,
    });
    return response.data;
  },
  deleteCredentials: async (provider) => {
    const response = await apiClient.delete(`/pos/credentials/${provider}/`);
    return response.data;
  },
};

const auth = {
  completeOnboarding: async () => {
    const response = await apiClient.post('/auth/onboarding/complete/');
    return response.data;
  },
};

// Organizations API
const organizations = {
  listUsers: async (params = {}) => {
    const response = await apiClient.get('/organizations/users/', { params });
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  updateUser: async (id, data) => {
    const response = await apiClient.patch(`/organizations/users/${id}/`, data);
    return response.data;
  },
  deactivateUser: async (id) => {
    await apiClient.delete(`/organizations/users/${id}/`);
  },
  activateUser: async (id) => {
    const response = await apiClient.patch(`/organizations/users/${id}/`, { is_active: true });
    return response.data;
  },
  deleteUser: async (id) => {
    await apiClient.delete(`/organizations/users/${id}/hard-delete/`);
  },
  listInvitations: async () => {
    const response = await apiClient.get('/organizations/invitations/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  createInvitation: async (email) => {
    const response = await apiClient.post('/organizations/invitations/', { email });
    return response.data;
  },
  deleteInvitation: async (id) => {
    await apiClient.delete(`/organizations/invitations/${id}/`);
  },
  verifyInvite: async (token) => {
    const response = await apiClient.get(`/auth/invite/${token}/verify/`);
    return response.data;
  },
  acceptInvite: async (token, data) => {
    const response = await apiClient.post(`/auth/invite/${token}/accept/`, data);
    return response.data;
  },
};

const tasks = {
  assign: async (taskId, employeeIds, dueDate) => {
    const response = await apiClient.post(`/tasks/${taskId}/assign/`, {
      employee_ids: employeeIds,
      ...(dueDate && { due_date: dueDate }),
    });
    return response.data;
  },
  ranking: async () => {
    const response = await apiClient.get('/tasks/ranking/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
};

// Forecast API
const forecast = {
  getRevenue: async (days = 7) => {
    try {
      const response = await apiClient.get('/forecast/revenue/', { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching revenue forecast:', error);
      return { forecasts: [], meta: {} };
    }
  },
  getProducts: async (days = 7) => {
    try {
      const response = await apiClient.get('/forecast/products/', { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching product forecast:', error);
      return { forecasts: [], meta: {} };
    }
  },
  syncWeather: async () => {
    try {
      const response = await apiClient.get('/forecast/weather/sync/');
      return response.data;
    } catch (error) {
      console.error('Error syncing weather:', error);
      return { backfill_records: 0, forecast_records: 0, errors: [] };
    }
  },
};

export const base44 = {
  auth,
  agents,
  entities,
  pos,
  organizations,
  taskRecipeAllocations,
  tasks,
  forecast,
};
